import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getUserId, planetGet, planetPost } from "../../api/planetApi";

const CITY_HINTS = ["合肥", "上海", "南昌", "重庆", "北京", "杭州", "成都", "武汉", "南京", "广州"];

export default function PlanetPrivacyPage() {
  const userId = getUserId();
  const [profile, setProfile] = useState(null);
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [nickname, setNickname] = useState("");
  const [interests, setInterests] = useState("");
  const [bio, setBio] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!userId) return;
    try {
      const data = await planetGet(`/profile/me?user_id=${userId}`);
      setProfile(data.profile);
      setCity(data.profile?.city || "");
      setProvince(data.profile?.province || "");
      setNickname(data.profile?.nickname || "");
      setInterests(data.profile?.interests || "");
      setBio(data.profile?.bio || "");
    } catch (e) {
      setMsg(e.message);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const saveLocation = async (alsoPublic) => {
    if (!userId) {
      setMsg("请先登录");
      return;
    }
    setLoading(true);
    setMsg("");
    try {
      const data = await planetPost("/location", {
        user_id: userId,
        city: city.trim(),
        province: province.trim(),
        location_public: alsoPublic,
      });
      setProfile(data.profile);
      setMsg(alsoPublic ? "已保存并公开到地图" : "位置已保存");
    } catch (e) {
      setMsg(e.message);
    } finally {
      setLoading(false);
    }
  };

  const togglePublic = async (next) => {
    if (!userId) return;
    setLoading(true);
    setMsg("");
    try {
      const data = await planetPost("/location/public", {
        user_id: userId,
        location_public: next,
      });
      setProfile(data.profile);
      setMsg(next ? "已公开模糊位置" : "已关闭，地图上将立即不可见");
    } catch (e) {
      setMsg(e.message);
    } finally {
      setLoading(false);
    }
  };

  const savePrefs = async (patch) => {
    if (!userId) return;
    try {
      const data = await planetPost("/profile", { user_id: userId, ...patch });
      setProfile(data.profile);
      setMsg("偏好已更新");
    } catch (e) {
      setMsg(e.message);
    }
  };

  if (!userId) {
    return (
      <p className="text-sm text-slate-600">
        请先 <Link className="text-teal-700 underline" to="/login">登录</Link> 后设置隐私与位置。
      </p>
    );
  }

  const pub = Boolean(profile?.location_public);

  return (
    <div className="max-w-lg space-y-8">
      <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <h2 className="text-lg font-medium text-teal-950">模糊城市位置</h2>
        <p className="text-sm text-slate-500">
          系统按城市中心做稳定抖动生成模糊坐标；公开 API 不返回精确定位。关闭后地图立即消失。
        </p>
        <label className="block text-sm">
          <span className="text-slate-500">城市</span>
          <input
            className="mt-1 w-full border rounded-lg px-3 py-2"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="如：合肥"
            list="planet-cities"
          />
          <datalist id="planet-cities">
            {CITY_HINTS.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </label>
        <label className="block text-sm">
          <span className="text-slate-500">省份（可选）</span>
          <input
            className="mt-1 w-full border rounded-lg px-3 py-2"
            value={province}
            onChange={(e) => setProvince(e.target.value)}
          />
        </label>
        {profile?.fuzzy_lat != null && (
          <p className="text-xs text-slate-400">
            当前模糊坐标：{profile.fuzzy_lat}, {profile.fuzzy_lng}
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={loading}
            onClick={() => saveLocation(false)}
            className="px-4 py-2 rounded-lg bg-slate-800 text-white text-sm"
          >
            仅保存
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={() => saveLocation(true)}
            className="px-4 py-2 rounded-lg bg-teal-700 text-white text-sm"
          >
            保存并公开
          </button>
          <button
            type="button"
            disabled={loading || !profile?.city}
            onClick={() => togglePublic(!pub)}
            className={`px-4 py-2 rounded-lg text-sm border ${
              pub ? "border-rose-300 text-rose-700" : "border-teal-300 text-teal-800"
            }`}
          >
            {pub ? "关闭地图可见" : "开启地图可见"}
          </button>
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
        <h2 className="text-lg font-medium text-teal-950">地图名片</h2>
        <p className="text-xs text-slate-500">公开后别人点标记可见昵称、兴趣与是否接瓶。</p>
        <label className="block text-sm">
          <span className="text-slate-500">昵称</span>
          <input
            className="mt-1 w-full border rounded-lg px-3 py-2"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            maxLength={40}
          />
        </label>
        <label className="block text-sm">
          <span className="text-slate-500">兴趣（逗号分隔）</span>
          <input
            className="mt-1 w-full border rounded-lg px-3 py-2"
            value={interests}
            onChange={(e) => setInterests(e.target.value)}
            placeholder="读书,徒步,电影"
          />
        </label>
        <label className="block text-sm">
          <span className="text-slate-500">一句话介绍</span>
          <textarea
            className="mt-1 w-full border rounded-lg px-3 py-2 min-h-[72px]"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            maxLength={200}
          />
        </label>
        <button
          type="button"
          className="px-4 py-2 rounded-lg bg-teal-700 text-white text-sm"
          onClick={() =>
            savePrefs({
              nickname: nickname.trim(),
              interests: interests.trim(),
              bio: bio.trim(),
            })
          }
        >
          保存名片
        </button>
      </section>

      <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
        <h2 className="text-lg font-medium text-teal-950">接收偏好</h2>
        {[
          ["accept_bottle", "接受漂流瓶"],
          ["accept_invite", "接受认识邀请"],
          ["accept_nearby", "接受同城投递"],
          ["accept_far", "接受远方随机"],
        ].map(([key, label]) => (
          <label key={key} className="flex items-center justify-between text-sm py-1">
            <span>{label}</span>
            <input
              type="checkbox"
              checked={Boolean(profile?.[key])}
              onChange={(e) => savePrefs({ [key]: e.target.checked })}
            />
          </label>
        ))}
      </section>

      {msg && <p className="text-sm text-teal-800">{msg}</p>}
    </div>
  );
}
